import React, { useState, useEffect } from 'react';
import UserGuidePage from './pages/UserGuidePage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import StrategyBuilderPage from './pages/StrategyBuilderPage';
import StrategySamplesPage from './pages/StrategySamplesPage';
import { runBacktest as apiRunBacktest, checkHealth } from './api';
import BacktestForm from './components/BacktestForm';
import ResultsDisplay from './components/ResultsDisplay';
import SimulateStrategy from './components/SimulateStrategy';
import PortfolioBacktest from './components/PortfolioBacktest';
import PortfolioBacktestPage from './pages/PortfolioBacktestPage';

// Categorized strategy samples for Strategy Samples tab
const strategyCategories = [
  {
    name: 'Trend Following',
    icon: '📈',
    color: '#34a853',
    strategies: [
      {
        title: 'Golden Cross',
        description: 'Buy when short MA crosses above long MA',
        entry: 'SMA(50) > SMA(200)',
        exit: 'SMA(50) < SMA(200)'
      },
      {
        title: 'Adaptive Strategy',
        description: 'Strategy that adapts to market conditions',
        entry: 'KAMA(14, 2, 30) > KAMA(50, 2, 30) AND ATR(14) > 2.0',
        exit: 'KAMA(14, 2, 30) < KAMA(50, 2, 30)'
      },
      {
        title: 'Supertrend Bullish',
        description: 'Buy when Supertrend turns bullish',
        entry: 'SUPERTREND(10, 3.0)',
        exit: 'SUPERTREND(10, 3.0) == 0'
      },
      {
        title: 'Triple EMA Stack',
        description: 'Buy when fast EMA > medium EMA > slow EMA',
        entry: 'EMA(12) > EMA(26) AND EMA(26) > EMA(50)',
        exit: 'EMA(12) < EMA(26)'
      },
      {
        title: 'ADX Strong Trend',
        description: 'Buy when ADX shows strong trend with price above MA',
        entry: 'ADX(14) > 25 AND CLOSE > SMA(20)',
        exit: 'ADX(14) < 20'
      },
      {
        title: 'Parabolic SAR Long',
        description: 'Follow trend using Parabolic SAR',
        entry: 'CLOSE > PSAR(0.02, 0.02, 0.2)',
        exit: 'CLOSE < PSAR(0.02, 0.02, 0.2)'
      }
    ]
  },
  {
    name: 'Mean Reversion',
    icon: '🔄',
    color: '#4285f4',
    strategies: [
      {
        title: 'Bollinger Band Reversal',
        description: 'Buy when price closes below lower band',
        entry: 'CLOSE < BB_LOWER(20, 2.0)',
        exit: 'CLOSE > BB_MIDDLE(20, 2.0)'
      },
      {
        title: 'RSI Oversold',
        description: 'Buy when RSI is below 30',
        entry: 'RSI(14) < 30',
        exit: 'RSI(14) > 50'
      },
      {
        title: 'Double Bollinger Reversal',
        description: 'Buy when price touches lower band with low RSI',
        entry: 'CLOSE < BB_LOWER(20, 2.0) AND RSI(14) < 35',
        exit: 'CLOSE > BB_UPPER(20, 2.0) OR RSI(14) > 65'
      },
      {
        title: 'Williams %R Reversal',  
        description: 'Buy when Williams %R shows oversold condition',
        entry: 'WILLIAMS_R(14) < -80',
        exit: 'WILLIAMS_R(14) > -20'
      },
      {
        title: 'CCI Mean Reversion',
        description: 'Buy when CCI indicates oversold market',
        entry: 'CCI(20) < -100',
        exit: 'CCI(20) > 100'
      }
    ]
  },
  {
    name: 'Breakout',
    icon: '🚀',
    color: '#ff9800',
    strategies: [
      {
        title: 'Donchian Channel Breakout',
        description: 'Buy when price breaks above channel',
        entry: 'CLOSE > DC_UPPER(20)',
        exit: 'CLOSE < DC_LOWER(20)'
      },
      {
        title: 'ATR Channel Breakout',
        description: 'Buy when price exceeds SMA(20) by ATR(14)',
        entry: 'CLOSE > SMA(20) + ATR(14)',
        exit: 'CLOSE < SMA(20) - ATR(14)'
      },
      {
        title: 'Bollinger Band Breakout',
        description: 'Buy on expansion breakout above upper band',
        entry: 'CLOSE > BB_UPPER(20, 2.0) AND VOLUME > SMA(20)',
        exit: 'CLOSE < BB_MIDDLE(20, 2.0)'
      },
      {
        title: 'Keltner Channel Breakout',
        description: 'Buy when price breaks above Keltner Channel',
        entry: 'CLOSE > KC_UPPER(20, 2.0)',
        exit: 'CLOSE < KC_LOWER(20, 2.0)'
      },
      {
        title: 'High Volume Breakout',
        description: 'Buy on price breakout with volume confirmation',
        entry: 'CLOSE > SMA(20) * 1.02 AND VOLUME > 2 * SMA(20)',
        exit: 'CLOSE < SMA(20) * 0.98'
      }
    ]
  },
  {
    name: 'Momentum',
    icon: '⚡',
    color: '#ea4335',
    strategies: [
      {
        title: 'RSI Bullish Momentum',
        description: 'Buy when RSI crosses above 50',
        entry: 'RSI(14) > 50',
        exit: 'RSI(14) < 50'
      },
      {
        title: 'MACD Crossover',
        description: 'Buy when MACD crosses above signal',
        entry: 'MACD(12,26,9) > MACD_SIGNAL(12,26,9)',
        exit: 'MACD(12,26,9) < MACD_SIGNAL(12,26,9)'
      },
      {
        title: 'Stochastic Momentum',
        description: 'Buy when Stochastic crosses above 20',
        entry: 'STOCH_K(14, 3) > STOCH_D(14, 3) AND STOCH_K(14, 3) > 20',
        exit: 'STOCH_K(14, 3) < STOCH_D(14, 3) AND STOCH_K(14, 3) > 80'
      },
      {
        title: 'ROC Momentum',
        description: 'Buy when Rate of Change shows strong momentum',
        entry: 'ROC(12) > 5 AND CLOSE > SMA(50)',
        exit: 'ROC(12) < -2'
      },
      {
        title: 'TSI Momentum',
        description: 'Buy when True Strength Index turns positive',
        entry: 'TSI(25, 13) > 0 AND TSI(25, 13) > TSI(25, 13)[-1]',
        exit: 'TSI(25, 13) < 0'
      },
      {
        title: 'Fisher Transform Signal',
        description: 'Buy when Fisher Transform crosses above zero',
        entry: 'FISHER(10) > 0 AND FISHER(10) > FISHER(10)[-1]',
        exit: 'FISHER(10) < 0'
      }
    ]
  },
  {
    name: 'Volume',
    icon: '📊',
    color: '#2196f3',
    strategies: [
      {
        title: 'Volume Spike',
        description: 'Buy when volume is 2x average',
        entry: 'VOLUME > 2 * SMA(20)',
        exit: 'VOLUME < SMA(20)'
      },
      {
        title: 'OBV Above Average',
        description: 'Buy when OBV is above its 10-day average',
        entry: 'OBV > SMA(10)',
        exit: 'OBV < SMA(10)'
      },
      {
        title: 'MFI Volume Flow',
        description: 'Buy when Money Flow Index shows buying pressure',
        entry: 'MFI(14) < 20 AND CLOSE > OPEN',
        exit: 'MFI(14) > 80'
      },
      {
        title: 'Chaikin Money Flow',
        description: 'Buy when CMF indicates accumulation',
        entry: 'CMF(20) > 0.1 AND CLOSE > SMA(20)',
        exit: 'CMF(20) < -0.1'
      },
      {
        title: 'VWAP Breakout',
        description: 'Buy when price breaks above VWAP with volume',
        entry: 'CLOSE > VWAP AND VOLUME > 1.5 * SMA(20)',
        exit: 'CLOSE < VWAP'
      }
    ]
  },
  {
    name: 'Scalping',
    icon: '⚡',
    color: '#9c27b0',
    strategies: [
      {
        title: 'EMA Scalp',
        description: 'Quick scalp using fast EMA crossover',
        entry: 'EMA(5) > EMA(13) AND RSI(14) > 50',
        exit: 'EMA(5) < EMA(13) OR RSI(14) < 30'
      },
      {
        title: 'Bollinger Scalp',
        description: 'Scalp between Bollinger Bands',
        entry: 'CLOSE < BB_LOWER(20, 1.5) AND RSI(14) < 40',
        exit: 'CLOSE > BB_UPPER(20, 1.5) OR RSI(14) > 60'
      },
      {
        title: 'Stochastic Scalp',
        description: 'Quick momentum scalp with Stochastic',
        entry: 'STOCH_K(5, 3) > STOCH_D(5, 3) AND STOCH_K(5, 3) < 80',
        exit: 'STOCH_K(5, 3) < STOCH_D(5, 3) OR STOCH_K(5, 3) > 90'
      }
    ]
  },
  {
    name: 'Swing Trading',
    icon: '🎯',
    color: '#607d8b',
    strategies: [
      {
        title: 'Weekly Swing',
        description: 'Medium-term swing using weekly signals',
        entry: 'CLOSE > SMA(50) AND RSI(14) > 45 AND RSI(14) < 65',
        exit: 'CLOSE < SMA(50) OR RSI(14) > 75'
      },
      {
        title: 'Support Bounce',
        description: 'Buy at support with confirmation',
        entry: 'CLOSE > SMA(200) AND CLOSE < SMA(20) * 0.98 AND RSI(14) < 40',
        exit: 'CLOSE > SMA(20) * 1.03 OR RSI(14) > 70'
      },
      {
        title: 'Pullback Entry',
        description: 'Buy pullbacks in uptrend',
        entry: 'SMA(20) > SMA(50) AND CLOSE < SMA(20) AND RSI(14) < 50',
        exit: 'CLOSE > SMA(20) * 1.05 OR RSI(14) < 30'
      }
    ]
  },
  {
    name: 'Advanced',
    icon: '🔬',
    color: '#795548',
    strategies: [
      {
        title: 'Multi-Timeframe Confluence',
        description: 'Complex strategy using multiple indicators',
        entry: 'CLOSE > EMA(21) AND RSI(14) > 50 AND MACD(12,26,9) > 0 AND VOLUME > SMA(20)',
        exit: 'CLOSE < EMA(21) OR RSI(14) < 30 OR MACD(12,26,9) < 0'
      },
      {
        title: 'Volatility Adjusted Entry',
        description: 'Entry based on volatility conditions',
        entry: 'CLOSE > SMA(20) + ATR(14) AND RSI(14) > 55 AND ADX(14) > 25',
        exit: 'CLOSE < SMA(20) - ATR(14) OR ADX(14) < 20'
      },
      {
        title: 'Statistical Arbitrage',
        description: 'Mean reversion with statistical validation',
        entry: '((CLOSE - SMA(20)) / SMA(20)) * 100 < -3 AND RSI(14) < 30',
        exit: '((CLOSE - SMA(20)) / SMA(20)) * 100 > 3 OR RSI(14) > 70'
      },
      {
        title: 'Adaptive Strategy',
        description: 'Strategy that adapts to market conditions',
        entry: 'KAMA(14, 2, 30) > KAMA(50, 2, 30) AND ATR(14) > 2.0',
        exit: 'KAMA(14, 2, 30) < KAMA(50, 2, 30)'
      }
    ]
  }
];export default function App() {
  // Set default start date to 2021-01-01 and end date to today
  const todayStr = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    symbol: 'RELIANCE',
    commission: 0.0001,
    timeframe: '1d',
    startDate: '2021-01-01',
    endDate: todayStr,
    startingEquity: 1000000,
    entryConditions: '',
    exitConditions: '',
    stopLoss: '',
    target: '',
    trailingStop: '',
    allocatedAmount: 0
  });

  const [results, setResults] = useState(null);
  const [showSimulator, setShowSimulator] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [apiStatus, setApiStatus] = useState('checking');
  const [error, setError] = useState(null);  
  const [activeTab, setActiveTab] = useState('builder'); // 'builder' or 'samples'
  // const [executionTime, setExecutionTime] = useState(null); // removed unused
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  
  // Indicator browser state
  const [indicatorSearch, setIndicatorSearch] = useState('');
  // const [indicatorCategory, setIndicatorCategory] = useState('all'); // removed unused
  const [indicatorSort, setIndicatorSort] = useState('name');
  
  // Strategy samples filter state
  const [selectedStrategyCategory, setSelectedStrategyCategory] = useState('all');

  const themeColors = {
    light: {
      bg: '#f5f5f5',
      cardBg: 'white',
      textPrimary: '#333',
      textSecondary: '#666',
      border: '#e0e0e0',
      inputBg: 'white'
    },
    dark: {
      bg: '#1a1a1a',
      cardBg: '#2d2d2d',
      textPrimary: '#ffffff',
      textSecondary: '#cccccc',
      border: '#404040',
      inputBg: '#333'
    }
  };

  const currentTheme = isDarkTheme ? themeColors.dark : themeColors.light;

  // Check API health on mount
  useEffect(() => {
    const checkApiHealth = async () => {
      try {
        await checkHealth();
        setApiStatus('connected');
      } catch (error) {
        setApiStatus('disconnected');
        console.error('API health check failed:', error);
      }
    };

    checkApiHealth();
  }, []);

  // Pre-built strategy templates
  const strategies = {
    trendFollowing: [
      { query: 'CLOSE > SMA(20)', description: 'Above 20-day MA' },
      { query: 'SMA(50) > SMA(200)', description: 'Golden Cross Setup' },
      { query: 'CLOSE > EMA(21)', description: 'Above 21-day EMA' },
      { query: 'CLOSE > SMA(20) and CLOSE > SMA(50)', description: 'Strong Uptrend' }
    ],
    bullishPatterns: [
      { query: 'CLOSE > OPEN and VOLUME > SMA(20)', description: 'Bullish Volume' },
      { query: 'LOW < SMA(20) and CLOSE > SMA(20)', description: 'Support Bounce' },
      { query: 'RSI(14) < 30 and CLOSE > OPEN', description: 'Oversold Reversal' }
    ],
    bearishPatterns: [
      { query: 'HIGH > SMA(20) and CLOSE < SMA(20)', description: 'Resistance Rejection' },
      { query: 'RSI(14) > 70 and CLOSE < OPEN', description: 'Overbought Reversal' },
      { query: 'CLOSE < SMA(50)', description: 'Below 50-day MA' }
    ],
    momentum: [
      { query: 'RSI(14) > 70', description: 'Overbought Territory' },
      { query: 'RSI(14) < 30', description: 'Oversold Territory' },
      { query: 'RSI(14) > 50', description: 'Bullish Momentum' }
    ],
    volumeAnalysis: [
      { query: 'VOLUME > SMA(20)', description: 'Above Average Volume' },
      { query: 'VOLUME > 2 * SMA(20)', description: '2x Normal Volume' },
      { query: 'CLOSE > OPEN and VOLUME > 1.5 * SMA(20)', description: 'Strong Buying' }
    ]
  };

  const categories = [
    { id: 'all', label: 'All', icon: '📊' },
    { id: 'trendFollowing', label: 'Trend Following', icon: '📈' },
    { id: 'bullishPatterns', label: 'Bullish Patterns', icon: '🟢' },
    { id: 'bearishPatterns', label: 'Bearish Patterns', icon: '🔴' },
    { id: 'momentum', label: 'Momentum', icon: '⚡' },
    { id: 'volumeAnalysis', label: 'Volume Analysis', icon: '📊' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const addToEntryConditions = (query) => {
    const currentValue = formData.entryConditions;
    const newValue = currentValue ? `${currentValue} and ${query}` : query;
    
    setFormData(prev => ({
      ...prev,
      entryConditions: newValue
    }));
  };

  const addToExitConditions = (query) => {
    const currentValue = formData.exitConditions;
    const newValue = currentValue ? `${currentValue} and ${query}` : query;
    
    setFormData(prev => ({
      ...prev,
      exitConditions: newValue
    }));
  };

  const runBacktest = async () => {
    // Validation
    if (!formData.entryConditions.trim()) {
      setError('Please add entry conditions');
      return;
    }
    if (!formData.exitConditions.trim()) {
      setError('Please add exit conditions');
      return;
    }

    setIsRunning(true);
    setError(null);
    setResults(null);
  // setExecutionTime(null); // removed unused
    
    const startTime = Date.now();
    
    try {
      const backtestData = {
        symbol: formData.symbol,
        timeframe: formData.timeframe,
        start_date: formData.startDate,
        end_date: formData.endDate,
        initial_capital: parseFloat(formData.startingEquity),
        commission: parseFloat(formData.commission),
        entry_conditions: formData.entryConditions,
        exit_conditions: formData.exitConditions,
        allocated_amount: parseFloat(formData.allocatedAmount),
        stop_loss: formData.stopLoss ? parseFloat(formData.stopLoss) : null,
        target: formData.target ? parseFloat(formData.target) : null
      };
      
      console.log('Running backtest with data:', backtestData);
      
      const result = await apiRunBacktest(backtestData, true);
      
      const endTime = Date.now();
      const executionTimeMs = endTime - startTime;
  // setExecutionTime(executionTimeMs); // removed unused
      
      console.log('Backtest result:', result);
      console.log(`Execution time: ${executionTimeMs}ms`);
      
      setResults({
        totalTrades: result.total_trades,
        winRate: result.win_rate.toFixed(1),
        profitFactor: result.profit_factor.toFixed(2),
        netProfit: (result.final_value - result.initial_capital).toFixed(2),
        maxDrawdown: result.max_drawdown.toFixed(1),
        sharpeRatio: result.sharpe_ratio.toFixed(2),
        equityCurve: result.equity_curve,
        trades: result.trades,
        executionTime: executionTimeMs,
        candles: result.candles // Pass candles for simulation
      });
      
    } catch (error) {
      console.error('Backtest failed:', error);
      setError(error.message || 'Backtest failed. Please check your conditions and try again.');
    } finally {
      setIsRunning(false);
    }
  };

  // Styles
  const cardStyle = {
    backgroundColor: currentTheme.cardBg,
    borderRadius: '8px',
    padding: '30px',
    boxShadow: isDarkTheme ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)',
    border: isDarkTheme ? `1px solid ${currentTheme.border}` : 'none'
  };

  const navLinkStyle = {
    textDecoration: 'none',
    color: currentTheme.textSecondary,
    fontSize: '14px',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  };

  const activeNavStyle = {
    backgroundColor: isDarkTheme ? '#404040' : '#f0f0f0',
    color: currentTheme.textPrimary,
    border: `1px solid ${currentTheme.border}`
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: currentTheme.bg, color: currentTheme.textPrimary }}>
      {/* Header */}
      <header style={{
        backgroundColor: currentTheme.cardBg,
        borderBottom: `1px solid ${currentTheme.border}`,
        padding: '15px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ fontSize: '18px', fontWeight: 500, margin: 0, color: currentTheme.textPrimary }}>
          Tern, A fintech Lab's No code Backtester APP
        </h1>
        <nav style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <span style={{ color: currentTheme.textSecondary, fontSize: '12px' }}>
            No Code Stock Technical Backtester
          </span>
          <span style={navLinkStyle}>Home</span>
          <span style={{ ...navLinkStyle, ...activeNavStyle }}>Backtesting</span>
          
          {/* API Status Indicator */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '12px',
            padding: '4px 10px',
            borderRadius: '12px',
            backgroundColor: apiStatus === 'connected' ? '#e6f4ea' : apiStatus === 'disconnected' ? '#fce8e6' : '#f1f3f4'
          }}>
            <span style={{ 
              width: '8px', 
              height: '8px', 
              borderRadius: '50%',
              backgroundColor: apiStatus === 'connected' ? '#0f9d58' : apiStatus === 'disconnected' ? '#d93025' : '#999'
            }} />
            <span style={{ 
              color: apiStatus === 'connected' ? '#0f9d58' : apiStatus === 'disconnected' ? '#d93025' : '#666'
            }}>
              {apiStatus === 'connected' ? 'API Connected' : apiStatus === 'disconnected' ? 'API Offline' : 'Checking...'}
            </span>
          </div>
          
          <button 
            onClick={() => setIsDarkTheme(!isDarkTheme)}
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              fontSize: '18px',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            {isDarkTheme ? '☀️' : '🌙'}
          </button>
        </nav>
      </header>

      {/* Main Content */}
      <div style={{ maxWidth: '100%', margin: '40px auto', padding: '0 20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 500, marginBottom: '10px', color: currentTheme.textPrimary }}>
            TERN - by Fintech Lab
          </h1>
        </div>

        {/* Error Alert */}
        {error && (
          <div style={{
            backgroundColor: isDarkTheme ? '#2d1b1b' : '#fce8e6',
            border: '1px solid #d93025',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <span style={{ color: '#d93025', fontSize: '14px' }}>{error}</span>
            <button 
              onClick={() => setError(null)}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#d93025'
              }}
            >
              ×
            </button>
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '30px',
          maxWidth: '100%',
          alignItems: 'flex-start'
        }}>
          {/* Left Panel - Configuration (Fixed Width, Auto Height) */}
          <div style={{
            ...cardStyle,
            width: '320px',
            flexShrink: 0
          }}>
            <BacktestForm 
              formData={formData}
              handleInputChange={handleInputChange}
              runBacktest={runBacktest}
              isRunning={isRunning}
              theme={currentTheme}
              isDarkTheme={isDarkTheme}
            />
          </div>

          {/* Right Panel - Tabs & Content (Flexible Width) */}
          <div style={{
            ...cardStyle,
            flex: 1,
            minWidth: 0
          }}>
            {/* Tab Navigation */}
            <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '20px' }}>
              <button
                onClick={() => setActiveTab('builder')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === 'builder' ? '2px solid #4285f4' : 'none',
                  color: activeTab === 'builder' ? '#4285f4' : currentTheme.textSecondary,
                  fontWeight: activeTab === 'builder' ? 600 : 400,
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  fontSize: '14px',
                  transition: 'color 0.2s'
                }}
              >
                🛠️ Strategy Builder
              </button>
              <button
                onClick={() => setActiveTab('samples')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === 'samples' ? '2px solid #4285f4' : 'none',
                  color: activeTab === 'samples' ? '#4285f4' : currentTheme.textSecondary,
                  fontWeight: activeTab === 'samples' ? 600 : 400,
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  fontSize: '14px',
                  transition: 'color 0.2s'
                }}
              >
                📚 Strategy Samples
              </button>
              <button
                onClick={() => setActiveTab('portfolio')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === 'portfolio' ? '2px solid #4285f4' : 'none',
                  color: activeTab === 'portfolio' ? '#4285f4' : currentTheme.textSecondary,
                  fontWeight: activeTab === 'portfolio' ? 600 : 400,
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  fontSize: '14px',
                  transition: 'color 0.2s'
                }}
              >
                📊 Portfolio Backtest
              </button>
              <button
                onClick={() => setActiveTab('expressions')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === 'expressions' ? '2px solid #4285f4' : 'none',
                  color: activeTab === 'expressions' ? '#4285f4' : currentTheme.textSecondary,
                  fontWeight: activeTab === 'expressions' ? 600 : 400,
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  fontSize: '14px',
                  transition: 'color 0.2s'
                }}
              >
                📋 User Guide
              </button>
              <button
                onClick={() => setActiveTab('knowledge')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === 'knowledge' ? '2px solid #4285f4' : 'none',
                  color: activeTab === 'knowledge' ? '#4285f4' : currentTheme.textSecondary,
                  fontWeight: activeTab === 'knowledge' ? 600 : 400,
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  fontSize: '14px',
                  transition: 'color 0.2s'
                }}
              >
                📖 Knowledge Base
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'builder' ? (
              <StrategyBuilderPage
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                strategies={strategies}
                categories={categories}
                formData={formData}
                handleInputChange={handleInputChange}
                addToEntryConditions={addToEntryConditions}
                addToExitConditions={addToExitConditions}
                theme={currentTheme}
                isDarkTheme={isDarkTheme}
              />
            ) : activeTab === 'samples' ? (
              <StrategySamplesPage
                strategyCategories={strategyCategories}
                selectedStrategyCategory={selectedStrategyCategory}
                setSelectedStrategyCategory={setSelectedStrategyCategory}
                setFormData={setFormData}
                setActiveTab={setActiveTab}
                currentTheme={currentTheme}
                isDarkTheme={isDarkTheme}
                indicatorSearch={indicatorSearch}
                setIndicatorSearch={setIndicatorSearch}
                indicatorSort={indicatorSort}
                setIndicatorSort={setIndicatorSort}
                // indicatorCategory={indicatorCategory} // removed unused
              />
            ) : activeTab === 'expressions' ? (
              <UserGuidePage currentTheme={currentTheme} isDarkTheme={isDarkTheme} />
            ) : activeTab === 'knowledge' ? (
              <KnowledgeBasePage currentTheme={currentTheme} isDarkTheme={isDarkTheme} />
            ) : activeTab === 'portfolio' ? (
              <PortfolioBacktestPage Component={PortfolioBacktest} />
            ) : (
              <div>
                {/* All Indicators Browser */}
                {/* ...existing code... */}
              </div>
            )}
            
            {/* Only show results in builder tab */}
            {activeTab === 'builder' && (
              <>
                <ResultsDisplay 
                  results={results} 
                  theme={currentTheme}
                  isDarkTheme={isDarkTheme}
                />
                {results && results.trades && results.equityCurve && (
                  <div style={{ textAlign: 'center', margin: '24px 0' }}>
                    <button
                      style={{ padding: '12px 32px', fontSize: '16px', background: '#4285f4', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      onClick={() => setShowSimulator(true)}
                    >
                      🚀 Simulate this Strategy
                    </button>
                  </div>
                )}
                {showSimulator && results && (
                  <SimulateStrategy
                    result={{
                      candles: results.candles || [],
                      trades: results.trades,
                      equityCurve: results.equityCurve || results.equity_curve || [],
                    }}
                    onBack={() => setShowSimulator(false)}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        textAlign: 'center',
        padding: '40px 20px',
        color: '#999',
        fontSize: '12px'
      }}>
        <p>
          Powered by Cygnus Technologies
        </p>
      </footer>
    </div>
  );
}